'use client';

import Link from '@eliadmin/core/src/components/link';

import type {
  GeneralLayout,
  Menu,
} from '@eliadmin/core/src/api/general/types/general-layout-types';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Logo } from '@eliadmin/core/src/components/ui/logo';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref } from '@eliadmin/core/src/utils/url-utils';
import { useEffect, useRef, useState } from 'react';
import MobileSidebar from '@eliadmin/core/src/components/header/mobile-sidebar';

export function HeaderClient({
  generalLayout,
  menu,
}: {
  generalLayout: GeneralLayout['data'];
  menu: Menu;
}) {
  const [scrolled, setScrolled] = useState(false);
  const scrolledRef = useRef(false);

  useEffect(() => {
    let frame: number | null = null;

    const update = () => {
      frame = null;
      const nextScrolled = window.scrollY > 20;

      if (nextScrolled === scrolledRef.current) return;
      scrolledRef.current = nextScrolled;
      setScrolled(nextScrolled);
    };

    const handleScroll = () => {
      if (frame === null) frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <header
      className={cn(
        'custom-section',
        'fixed left-0 top-0 z-40 w-full bg-transparent transition-[background-color,padding,box-shadow] duration-300 ease-in-out',
        scrolled ? 'bg-white py-4 sm:py-3' : 'py-[13px] sm:py-5 md:py-6 lg:py-8',
      )}
      style={{
        boxShadow: scrolled ? '0px 4px 32px 0px #0000001F' : '0px 4px 32px 0px #00000000',
      }}
    >
      <div className={cn('custom-container', 'flex items-center justify-between')}>
        <div className="flex items-center justify-start gap-4 lg:flex-1">
          <div className="flex items-center justify-center lg:hidden">
            <MobileSidebar
              menu={menu.fields}
              generalLayout={generalLayout}
              side="left"
              scrolled={scrolled}
            />
          </div>
          <Logo
            className={{ image: 'h-6 sm:h-7 lg:h-8' }}
            generalLayout={generalLayout}
            src={scrolled ? generalLayout?.footer?.logo?.url : generalLayout?.header?.logo?.url}
          />
        </div>
        <div className="hidden items-center justify-center gap-8 lg:flex">
          {menu?.fields?.map(field => (
            <Link
              key={field.name}
              href={getSafeHref(field?.path ?? '#')}
              className={cn(
                'text-sm text-white transition-[color,opacity] hover:opacity-80 lg:text-base',
                scrolled && 'text-black',
              )}
            >
              {field.name}
            </Link>
          ))}
        </div>
        <div className="flex flex-1 items-center justify-end gap-8">
          <Link
            href={getSafeHref('#kontakt')}
            className={cn(
              'hidden text-sm text-white transition-[color,opacity] hover:opacity-80 lg:block lg:text-base',
              scrolled && 'text-black',
            )}
          >
            Kontakt
          </Link>
          <Button asChild className="whitespace-nowrap">
            <Link href={getSafeHref('#kontakt')}>Konzultácia zdarma</Link>
          </Button>
        </div>
      </div>
    </header>
  );
}
